import type { RgbColor } from '../color/types';
import type { GridRenderer, RenderContext2D, RenderOptions } from './types';
import { getVisibleCellBounds } from './viewport';

/**
 * Seuil de pixels-écran par perle sous lequel on bascule du mode `pearl`
 * (cercle + trou) vers `flat` (rectangle plein) même si `mode` vaut `pearl`.
 *
 * En dessous de ~4 px, l'anneau perle fait < 1 px de large et le trou
 * devient invisible : aucune perte visuelle, gros gain perf (1 fillRect
 * par cellule au lieu de 2 arc + 2 styles + 1 fill).
 *
 * Conforme §5.3 du cahier des charges (LOD : Level of Detail).
 */
const MIN_PEARL_SCREEN_PX = 4;

function rgbToCSS(rgb: RgbColor): string {
  return `rgb(${rgb.r}, ${rgb.g}, ${rgb.b})`;
}

/**
 * Renderer Canvas 2D pour une grille de perles.
 *
 * Algorithme de rendu :
 *  1. clear()
 *  2. Pour chaque cellule du pattern : dessin selon mode (pearl/flat)
 *  3. Si `showPlateBorders`, dessine les bords de plaque en pointillés
 *  4. Si `currentPlate` fourni, surligne la plaque courante (fill semi-transparent)
 *
 * Optimisations futures (lot 2.C) : viewport culling, OffscreenCanvas, LOD.
 */
export class Canvas2DRenderer implements GridRenderer {
  private width: number;
  private height: number;

  constructor(
    private readonly ctx: RenderContext2D,
    width: number,
    height: number,
  ) {
    this.width = width;
    this.height = height;
  }

  clear(): void {
    this.ctx.clearRect(0, 0, this.width, this.height);
  }

  resize(width: number, height: number): void {
    this.width = width;
    this.height = height;
  }

  draw(options: RenderOptions): void {
    this.clear();
    this.drawCells(options);
    if (options.showPlateBorders) {
      this.drawPlateBorders(options);
      if (options.currentPlate) {
        this.highlightCurrentPlate(options);
      }
    }
    if (options.currentLine) {
      this.drawCurrentLine(options);
    }
    if (options.toolPreview) {
      this.drawToolPreview(options);
    }
  }

  // --- Internes ---------------------------------------------------------

  private toScreenX(worldX: number, viewport: RenderOptions['viewport']): number {
    return (worldX + viewport.offsetX) * viewport.scale;
  }
  private toScreenY(worldY: number, viewport: RenderOptions['viewport']): number {
    return (worldY + viewport.offsetY) * viewport.scale;
  }

  private drawCells(options: RenderOptions): void {
    const { pattern, viewport, cellSize, mode } = options;
    const screenSize = cellSize * viewport.scale;

    // LOD : sous le seuil, le mode pearl est imperceptible. On bascule en flat.
    const effectiveMode: typeof mode =
      mode === 'pearl' && screenSize >= MIN_PEARL_SCREEN_PX ? 'pearl' : 'flat';

    // Viewport culling : ne traiter que les cellules visibles à l'écran.
    const bounds = getVisibleCellBounds(
      viewport,
      this.width,
      this.height,
      cellSize,
      pattern.width,
      pattern.height,
    );

    if (effectiveMode === 'flat') {
      this.drawCellsFlat(options, bounds);
    } else {
      this.drawCellsPearl(options, bounds);
    }
  }

  /**
   * Mode flat : regroupe les cellules par couleur dans des `Path2D` (1 par
   * couleur). Au lieu de N `fillRect`, on fait `fill(path)` 1 fois par couleur.
   * Sur 90k cellules × 22 couleurs : 22 appels Canvas au lieu de 90 000.
   */
  private drawCellsFlat(
    options: RenderOptions,
    bounds: { minCol: number; maxCol: number; minRow: number; maxRow: number },
  ): void {
    const { pattern, palette, viewport, cellSize, overrides } = options;
    const screenSize = cellSize * viewport.scale;

    const pathsByColor = new Map<number, Path2D>();

    for (let row = bounds.minRow; row <= bounds.maxRow; row++) {
      for (let col = bounds.minCol; col <= bounds.maxCol; col++) {
        const i = row * pattern.width + col;
        const idx = overrides?.get(i) ?? pattern.cells[i];
        if (idx === undefined) continue;
        if (palette[idx] === undefined) continue;

        const sx = this.toScreenX(col * cellSize, viewport);
        const sy = this.toScreenY(row * cellSize, viewport);

        let path = pathsByColor.get(idx);
        if (!path) {
          path = new Path2D();
          pathsByColor.set(idx, path);
        }
        path.rect(sx, sy, screenSize, screenSize);
      }
    }

    for (const [idx, path] of pathsByColor) {
      const bead = palette[idx];
      if (bead === undefined) continue;
      this.ctx.fillStyle = rgbToCSS(bead.rgb);
      this.ctx.fill(path);
    }
  }

  /**
   * Mode pearl : regroupe les anneaux par couleur (1 Path2D par couleur, stroke
   * batché) et tous les trous dans un seul Path2D (1 fill global).
   *
   * Géométrie Hama Midi (§14.1) :
   *  - perle 5 mm extérieur, trou 2.5 mm → trou = 50%
   *  - marge 5% entre perles voisines (outerRadius = 0.95 × demi-cellule)
   *
   * Sur 90k cellules × 22 couleurs : 22 stroke + 1 fill = 23 appels Canvas
   * au lieu de 360 000 (4 ops Canvas × 90k cells).
   */
  private drawCellsPearl(
    options: RenderOptions,
    bounds: { minCol: number; maxCol: number; minRow: number; maxRow: number },
  ): void {
    const { pattern, palette, viewport, cellSize, theme, overrides } = options;
    const screenSize = cellSize * viewport.scale;
    const halfSize = screenSize / 2;
    const outerRadius = halfSize * 0.95;
    const innerRadius = outerRadius * 0.5;
    const ringWidth = Math.max(0.5, outerRadius - innerRadius);
    const pathRadius = outerRadius - ringWidth / 2;
    const drawHoles = innerRadius > 0;

    const ringsByColor = new Map<number, Path2D>();
    const holes = drawHoles ? new Path2D() : null;

    for (let row = bounds.minRow; row <= bounds.maxRow; row++) {
      for (let col = bounds.minCol; col <= bounds.maxCol; col++) {
        const i = row * pattern.width + col;
        const idx = overrides?.get(i) ?? pattern.cells[i];
        if (idx === undefined) continue;
        if (palette[idx] === undefined) continue;

        const sx = this.toScreenX(col * cellSize, viewport);
        const sy = this.toScreenY(row * cellSize, viewport);
        const cx = sx + halfSize;
        const cy = sy + halfSize;

        let ring = ringsByColor.get(idx);
        if (!ring) {
          ring = new Path2D();
          ringsByColor.set(idx, ring);
        }
        // moveTo coupe le sub-path précédent → arc isolé (pas relié au suivant)
        ring.moveTo(cx + pathRadius, cy);
        ring.arc(cx, cy, pathRadius, 0, Math.PI * 2);

        if (holes) {
          holes.moveTo(cx + innerRadius, cy);
          holes.arc(cx, cy, innerRadius, 0, Math.PI * 2);
        }
      }
    }

    this.ctx.lineWidth = ringWidth;
    for (const [idx, ring] of ringsByColor) {
      const bead = palette[idx];
      if (bead === undefined) continue;
      this.ctx.strokeStyle = rgbToCSS(bead.rgb);
      this.ctx.stroke(ring);
    }

    if (holes) {
      this.ctx.fillStyle = theme.beadHole;
      this.ctx.fill(holes);
    }
  }

  private drawPlateBorders(options: RenderOptions): void {
    const { pattern, viewport, cellSize, theme } = options;
    const { plateWidth, plateHeight, cols, rows } = pattern.layout;
    if (plateWidth <= 0 || plateHeight <= 0) return;

    this.ctx.save();
    this.ctx.strokeStyle = theme.plateBorder;
    this.ctx.lineWidth = Math.max(1, viewport.scale);
    this.ctx.setLineDash([8, 5]);

    for (let r = 0; r < rows; r++) {
      for (let c = 0; c < cols; c++) {
        const x = this.toScreenX(c * plateWidth * cellSize, viewport);
        const y = this.toScreenY(r * plateHeight * cellSize, viewport);
        const w = plateWidth * cellSize * viewport.scale;
        const h = plateHeight * cellSize * viewport.scale;
        this.ctx.strokeRect(x, y, w, h);
      }
    }

    this.ctx.setLineDash([]);
    this.ctx.restore();
  }

  /**
   * Indicateur visuel "où va frapper le prochain clic" :
   *  - brush-rect : cadre pointillé accent autour du carré de pinceau / gomme
   *  - fill-cells : fill semi-transparent accent sur toutes les cellules
   *    qui seront remplies par le pot (résultat précalculé du flood fill)
   */
  private drawToolPreview(options: RenderOptions): void {
    const tp = options.toolPreview;
    if (!tp) return;
    const { viewport, cellSize, theme } = options;
    const screenSize = cellSize * viewport.scale;

    if (tp.kind === 'brush-rect') {
      this.ctx.save();
      const x = this.toScreenX(tp.minCol * cellSize, viewport);
      const y = this.toScreenY(tp.minRow * cellSize, viewport);
      const w = (tp.maxCol - tp.minCol + 1) * cellSize * viewport.scale;
      const h = (tp.maxRow - tp.minRow + 1) * cellSize * viewport.scale;
      this.ctx.strokeStyle = theme.plateActive;
      this.ctx.lineWidth = Math.max(1.5, viewport.scale * 0.5);
      this.ctx.setLineDash([6, 4]);
      this.ctx.strokeRect(x, y, w, h);
      this.ctx.setLineDash([]);
      this.ctx.restore();
      return;
    }

    // fill-cells : fill semi-transparent en couleur accent
    if (tp.cells.length === 0) return;
    this.ctx.save();
    this.ctx.globalAlpha = 0.4;
    this.ctx.fillStyle = theme.plateActive;
    const path = new Path2D();
    for (const c of tp.cells) {
      const x = this.toScreenX(c.col * cellSize, viewport);
      const y = this.toScreenY(c.row * cellSize, viewport);
      path.rect(x, y, screenSize, screenSize);
    }
    this.ctx.fill(path);
    this.ctx.restore();
  }

  private highlightCurrentPlate(options: RenderOptions): void {
    const cp = options.currentPlate;
    if (!cp) return;
    const { pattern, viewport, cellSize, theme } = options;
    const { plateWidth, plateHeight } = pattern.layout;

    this.ctx.save();
    this.ctx.globalAlpha = 0.18;
    this.ctx.fillStyle = theme.plateActive;
    const x = this.toScreenX(cp.col * plateWidth * cellSize, viewport);
    const y = this.toScreenY(cp.row * plateHeight * cellSize, viewport);
    const w = plateWidth * cellSize * viewport.scale;
    const h = plateHeight * cellSize * viewport.scale;
    this.ctx.fillRect(x, y, w, h);
    this.ctx.globalAlpha = 1;
    this.ctx.restore();
  }

  /**
   * Surligne la ligne ou colonne en cours de pose (F5 mode guidé).
   * Alpha plus marqué (0.35) que le highlight de plaque pour rendre la rangée
   * immédiatement repérable. Les bornes inclusives min/max permettent de
   * clamper le strip aux limites de la plaque physique courante.
   */
  private drawCurrentLine(options: RenderOptions): void {
    const cl = options.currentLine;
    if (!cl) return;
    const { viewport, cellSize, theme } = options;
    const cellScreen = cellSize * viewport.scale;

    this.ctx.save();
    this.ctx.globalAlpha = 0.35;
    this.ctx.fillStyle = theme.plateActive;

    if (cl.axis === 'row') {
      const x = this.toScreenX(cl.minCol * cellSize, viewport);
      const y = this.toScreenY(cl.row * cellSize, viewport);
      const w = (cl.maxCol - cl.minCol + 1) * cellScreen;
      this.ctx.fillRect(x, y, w, cellScreen);
    } else {
      const x = this.toScreenX(cl.col * cellSize, viewport);
      const y = this.toScreenY(cl.minRow * cellSize, viewport);
      const h = (cl.maxRow - cl.minRow + 1) * cellScreen;
      this.ctx.fillRect(x, y, cellScreen, h);
    }

    this.ctx.globalAlpha = 1;
    this.ctx.restore();
  }
}
